import * as React from 'react'

import { markNavSeen, navCounters } from '@/data/api'
import { isMockActive } from '@/data/mock/mode'
import type { NavCounterRow } from '@/data/types'
import { onLocalWrite } from '@/data/use-load'
import { onLiveChange } from '@/lib/realtime'
import { isViewingAs } from '@/lib/view-as'

import { toCounterMap, type NavCounters } from './nav-counters'

/** Rajada de mudanças (tempo real, escrita própria) vira UMA leitura depois disso. */
const DEBOUNCE_MS = 1_500
/** Sem mudança nenhuma, relê de tempos em tempos: "vence hoje" vira "atrasada" na virada do dia. */
const POLL_MS = 5 * 60_000

/**
 * Contadores da barra lateral (SIS-508): uma chamada a `supply.nav_counters()`
 * e releitura com debounce quando o tempo real entrega mudança, quando a
 * pessoa grava (`invalidate()`) e quando a aba volta a ter foco.
 *
 * `activeRoute` = rota do menu aberta: ao entrar e ao sair dela grava o
 * "visto" (`mark_nav_seen`) e zera a novidade na hora (otimista). No "ver
 * como" (somente leitura) e com dados fictícios nada é gravado.
 *
 * Falha é silenciosa: fica o último valor que chegou; badge nunca quebra a sidebar.
 */
export function useNavCounters(activeRoute: string | null): NavCounters {
  const [rows, setRows] = React.useState<readonly NavCounterRow[]>([])
  const reloadRef = React.useRef<() => void>(() => undefined)

  React.useEffect(() => {
    // Dados fictícios (SIS-465): o banco em memória não tem a RPC; sem badges.
    if (isMockActive()) return
    let cancelled = false
    let timer: number | null = null
    let seq = 0

    const load = async () => {
      const mine = ++seq
      try {
        const next = await navCounters()
        if (!cancelled && mine === seq) setRows(next)
      } catch {
        /* silencioso: mantém o último valor */
      }
    }
    const schedule = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        timer = null
        void load()
      }, DEBOUNCE_MS)
    }
    reloadRef.current = schedule

    void load()
    const offLive = onLiveChange(schedule)
    const offWrite = onLocalWrite(schedule)
    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule()
    }
    document.addEventListener('visibilitychange', onVisible)
    const poll = window.setInterval(schedule, POLL_MS)
    return () => {
      cancelled = true
      reloadRef.current = () => undefined
      offLive()
      offWrite()
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(poll)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [])

  // Entrou numa rota: visto agora. Saiu: visto de novo (o que chegou enquanto estava lá foi visto).
  React.useEffect(() => {
    if (!activeRoute || isMockActive() || isViewingAs()) return
    const route = activeRoute
    const seen = () => {
      setRows((prev) => prev.map((r) => (r.route === route && r.novidades ? { ...r, novidades: 0 } : r)))
      markNavSeen(route)
        .then((r) => r.ok && reloadRef.current())
        .catch(() => undefined)
    }
    seen()
    return seen
  }, [activeRoute])

  return React.useMemo(() => toCounterMap(rows), [rows])
}
